import React, { useCallback, useMemo, useState } from 'react';

import { Radio, Typography, Alert } from 'antd';

import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { formatCurrency } from '@utils/formatters';
import { IDevice } from '@pages/Assets/Inventory/types';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import ActionTypes from '@utils/deviceActions';
import { IDeviceActionRequestPayload } from '@components/DeviceActionModal/types';

import * as S from './styles';

const { Text } = Typography;

type BrokerCancellationType = 'cancellation' | 'withdraw' | 'replacement';

interface BrokerCancellationProps {
  actionKey: number;
  devicesProps: IDevice[] | undefined;
  uploadedDevices: IDevice[] | undefined;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
}

const useBrokerCancellation = ({
  actionKey,
  devicesProps,
  uploadedDevices,
  handleDeviceActions,
}: BrokerCancellationProps) => {
  const { t } = useTranslation('device-actions-modal');
  const [cancellationType, setCancellationType] =
    useState<BrokerCancellationType>('cancellation');

  const devices = useMemo(
    () =>
      (devicesProps && devicesProps.length > 0
        ? devicesProps
        : uploadedDevices
      )?.map(device => device.msisdn),
    [devicesProps, uploadedDevices],
  );

  const { data: penaltyData } = useSwr(
    actionKey === ActionTypes.BROKER_CANCELLATION &&
      devices &&
      devices?.length > 0
      ? '/service-proxy/broker/cancellation/calculate-fee'
      : null,
    {
      method: 'post',
      data: devices,
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
    },
  );

  const extraAction = useMemo(() => {
    if (cancellationType === 'replacement') {
      return ActionTypes.BROKER_SUBSTITUTION;
    }
    if (cancellationType === 'withdraw') {
      return ActionTypes.BROKER_CANCELLATION_WAIVER;
    }
    return ActionTypes.BROKER_CANCELLATION;
  }, [cancellationType]);

  const onSubmit = useCallback(() => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = extraAction;
    payload.devices = devices?.map(msisdn => ({
      msisdn,
    }));
    return handleDeviceActions(payload);
  }, [extraAction, devices, handleDeviceActions]);

  const contentSteps = [
    {
      title: t('steps.cancellation.action'),
      content: (
        <S.ContainerAction>
          <Radio.Group
            className="action-lock-unlock"
            value={cancellationType}
            onChange={({ target: { value } }) => {
              setCancellationType(value);
            }}
          >
            <Radio.Button
              value="cancellation"
              className="action-lock-unlock-radio"
            >
              <LockOutlined />
              {t('steps.cancellation.cancel')}
            </Radio.Button>
            <Radio.Button value="withdraw" className="action-lock-unlock-radio">
              <UnlockOutlined />
              {t('steps.cancellation.withdraw')}
            </Radio.Button>
          </Radio.Group>
        </S.ContainerAction>
      ),
    },
    {
      title: t('steps.cancellation.informative'),
      content: (
        <S.ContainerInfo>
          {cancellationType === 'withdraw' ? (
            <Alert
              style={{
                padding: 16,
              }}
              description={
                <Text style={{ fontSize: '0.8rem' }}>
                  {t('steps.cancellation.withdraw-info')}
                </Text>
              }
              type="info"
              showIcon
            />
          ) : (
            <>
              <S.ContainerReplacement>
                <Text>{t('steps.cancellation.replacement-info')}</Text>
                <S.ReplacementChoice>
                  <Radio.Group
                    value={cancellationType}
                    onChange={({ target: { value } }) => {
                      setCancellationType(value);
                    }}
                  >
                    <Radio.Button value="cancellation">
                      {t('steps.cancellation.cancel')}
                    </Radio.Button>
                    <Radio.Button value="replacement">
                      {t('steps.cancellation.replace')}
                    </Radio.Button>
                  </Radio.Group>
                </S.ReplacementChoice>
              </S.ContainerReplacement>
              {cancellationType === 'replacement' ? (
                <Alert
                  style={{
                    padding: 16,
                    marginTop: 16,
                  }}
                  description={
                    <Text style={{ fontSize: '0.8rem' }}>
                      {t('steps.cancellation.hours-of-completion', {
                        value: 72,
                      })}
                    </Text>
                  }
                  type="info"
                  showIcon
                />
              ) : (
                <>
                  <S.CancellationValue>
                    {`${t('steps.cancellation.cancellation-rate')}:`}
                    <Text strong>{formatCurrency(penaltyData?.fee ?? 0)}</Text>
                  </S.CancellationValue>
                  <Alert
                    style={{
                      padding: 16,
                    }}
                    description={
                      <Text style={{ fontSize: '0.8rem' }}>
                        <Text strong>
                          {t('steps.cancellation.hours-of-completion', {
                            value: 48,
                          })}
                          <br />
                        </Text>
                        {t('steps.cancellation.cancel-info')}
                      </Text>
                    }
                    type="info"
                    showIcon
                  />
                </>
              )}
            </>
          )}
        </S.ContainerInfo>
      ),
    },
  ];

  const reinitState = useCallback(() => {
    setCancellationType('cancellation');
  }, []);

  const brokerCancellationData = {
    onSubmit,
    reinitState,
    extraAction,
    contentSteps,
  };

  return { brokerCancellationData };
};

export default useBrokerCancellation;
